import React, { useState, useEffect } from 'react';
import { getProxiedCloudinaryUrl } from '../utils/cloudinaryProxy';
import { getCurrencySymbol } from '../utils/config';
import './ItemDetailModal.css';

const ItemDetailModal = ({ item, isOpen, onClose, onAddToCart }) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedVariations, setSelectedVariations] = useState([]);
  const [variations, setVariations] = useState([]);
  const [loadingVariations, setLoadingVariations] = useState(false);
  const [orderType, setOrderType] = useState('Dine In');
  const [currencySymbol, setCurrencySymbol] = useState('RM');

  useEffect(() => {
    if (isOpen && item) {
      fetchItemVariations();
      setQuantity(1);
      setSelectedVariations([]);
      setOrderType('Dine In');
    }
  }, [isOpen, item]);

  useEffect(() => {
    const loadCurrency = async () => {
      const symbol = await getCurrencySymbol();
      setCurrencySymbol(symbol);
    };
    loadCurrency();
  }, []);

  const fetchItemVariations = async () => {
    if (!item) return;

    setLoadingVariations(true);
    try {
      const response = await fetch(`/api/menu-item-variations/${item.ITEM_ID}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setVariations(processVariationsData(data.data));
        }
      }
    } catch (error) {
      console.error('Error fetching variations:', error);
    } finally {
      setLoadingVariations(false);
    }
  };

  const processVariationsData = (rawVariations) => {
    const variationGroups = {};

    rawVariations.forEach(variation => {
      const groupId = variation.VARIATION_GROUP_ID || variation.NAME;
      if (!variationGroups[groupId]) {
        variationGroups[groupId] = {
          id: groupId,
          name: variation.GROUP_NAME || variation.NAME,
          isRequired: variation.IS_REQUIRED || false,
          minSelections: variation.MIN_SELECTIONS || (variation.IS_REQUIRED ? 1 : 0),
          maxSelections: variation.MAX_SELECTIONS || 0,
          options: []
        };
      }

      const price = parseFloat(variation.PRICE || variation.MIN_PRICE || 0);
      variationGroups[groupId].options.push({
        id: variation.VARIATION_ID,
        name: variation.VARIATION_NAME || variation.NAME,
        price: price,
        isDefault: variation.IS_DEFAULT || false
      });
    });

    return Object.values(variationGroups);
  };

  const handleVariationChange = (groupId, optionId, checked) => {
    setSelectedVariations(prev => {
      const newVariations = prev.filter(v => v.groupId !== groupId);
      if (checked) {
        const group = variations.find(g => g.id === groupId);
        const option = group?.options.find(o => o.id === optionId);
        if (option) {
          newVariations.push({
            groupId,
            groupName: group.name,
            ...option
          });
        }
      }
      return newVariations;
    });
  };

  const calculateTotalPrice = () => {
    const basePrice = parseFloat(item.BASE_PRICE || 0);
    const variationsTotal = selectedVariations.reduce((sum, v) => sum + v.price, 0);
    return (basePrice + variationsTotal) * quantity;
  };

  const handleAddToCart = () => {
    // Validate required variations
    const requiredGroups = variations.filter(g => g.isRequired);
    const missingRequired = requiredGroups.filter(group =>
      !selectedVariations.some(v => v.groupId === group.id)
    );

    if (missingRequired.length > 0) {
      alert(`Please select options for: ${missingRequired.map(g => g.name).join(', ')}`);
      return;
    }

    const totalPrice = calculateTotalPrice() / quantity; // Price per item
    const cartItem = {
      ...item,
      quantity,
      selectedVariations,
      orderType,
      totalPrice: totalPrice, // Price per item
      calculatedPrice: calculateTotalPrice() // Total for quantity
    };

    console.log('ItemDetailModal - Adding to cart:', cartItem);
    console.log('Selected variations:', selectedVariations);
    console.log('Total price per item:', totalPrice);

    onAddToCart(cartItem);
    onClose();
  };

  if (!isOpen || !item) return null;

  const rawImageUrl = item.IMAGE_URL || '../img/dish.png';
  const imageUrl = getProxiedCloudinaryUrl(rawImageUrl);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content item-detail-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-item-image" style={{ backgroundImage: `url('${imageUrl}')` }}></div>

        <div className="item-detail-content">
          <h5 className="item-detail-name">{item.NAME}</h5>
          <span className="item-detail-code">{item.ITEM_CODE || 'No Code'} - {item.DESCRIPTION || 'No description'}</span>

          <div className="quantity-control">
            <button className="quantity-btn" onClick={() => setQuantity(Math.max(1, quantity - 1))}>
              <span>−</span>
            </button>
            <span className="quantity-value">{quantity}</span>
            <button className="quantity-btn" onClick={() => setQuantity(quantity + 1)}>
              <span>+</span>
            </button>

            <div className="order-type-selector">
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="orderType"
                  id="dineIn"
                  value="Dine In"
                  checked={orderType === 'Dine In'}
                  onChange={(e) => setOrderType(e.target.value)}
                />
                <label className="form-check-label" htmlFor="dineIn">Dine In</label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="orderType"
                  id="takeAway"
                  value="Take Away"
                  checked={orderType === 'Take Away'}
                  onChange={(e) => setOrderType(e.target.value)}
                />
                <label className="form-check-label" htmlFor="takeAway">Take Away</label>
              </div>
            </div>
          </div>

          <div className="item-variations-container">
            {loadingVariations ? (
              <div className="variations-loading">Loading options...</div>
            ) : (
              variations.map(group => (
                <div key={group.id} className="variation-group">
                  <div className="variation-group-header">
                    <h6>{group.name}</h6>
                    {group.isRequired && <span className="variation-required">Required</span>}
                    {group.minSelections > 0 && <span className="variation-min">Min: {group.minSelections}</span>}
                    {group.maxSelections > 0 && <span className="variation-max">Max: {group.maxSelections}</span>}
                  </div>

                  <div className="variation-options">
                    {group.options.map(option => (
                      <div key={option.id} className="variation-option">
                        <label className="variation-option-label">
                          <input
                            type="checkbox"
                            name={`variation_${group.id}`}
                            value={option.id}
                            onChange={(e) => handleVariationChange(group.id, option.id, e.target.checked)}
                          />
                          <span className="variation-option-name">{option.name}</span>
                          <span className="variation-option-price">
                            {option.price > 0 ? `+${currencySymbol}${option.price.toFixed(2)}` : ''}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="item-detail-price">
            Total: {currencySymbol}{calculateTotalPrice().toFixed(2)}
          </div>

          <div className="modal-item-buttons">
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
            <button className="btn-add-to-cart" onClick={handleAddToCart}>
              Add to Cart - {currencySymbol}{calculateTotalPrice().toFixed(2)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailModal;